import { useState, useEffect, useRef } from 'react';

export interface UseAsyncWithDefaultOptions<T> {
  /** Default value to return while loading or on error */
  defaultValue: T;
  /** Dependencies array - when these change, the async function will be re-executed */
  deps: React.DependencyList;
  /** Optional error handler */
  onError?: (error: Error) => void;
}

export interface UseAsyncWithDefaultResult<T> {
  /** The current value (either the result or defaultValue) */
  value: T;
  /** Whether the async operation is currently running */
  isLoading: boolean;
  /** The last error that occurred, if any */
  error: Error | null;
}

/**
 * Hook for managing async operations with a default value.
 * 
 * @param asyncFn Function that returns a Promise
 * @param options Configuration options
 * @returns Object with value, isLoading, and error
 */
export function useAsyncWithDefault<T>(
  asyncFn: () => Promise<T>,
  options: UseAsyncWithDefaultOptions<T>
): UseAsyncWithDefaultResult<T> {
  const { defaultValue, deps, onError } = options;
  const [value, setValue] = useState<T>(defaultValue);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Use ref to track the latest request to avoid race conditions
  const requestIdRef = useRef(0);

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;
    
    setIsLoading(true);
    setError(null);
    
    asyncFn()
      .then((result) => {
        // Only update if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setValue(result);
          setError(null);
        }
      })
      .catch((err) => {
        // Only update if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          const error = err instanceof Error ? err : new Error(String(err));
          setError(error);
          setValue(defaultValue);
          onError?.(error);
        }
      })
      .finally(() => {
        // Only update if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
  }, deps);

  // Reset to default value when dependencies become empty/invalid
  useEffect(() => {
    if (deps.some(dep => dep === null || dep === undefined)) {
      setValue(defaultValue);
      setError(null);
      setIsLoading(false);
    }
  }, deps);

  return { value, isLoading, error };
}
